import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAddressLatLng, useAddressNotAcc, useCheckDistance, useGeocode, useI18n, useLocationLoading, useMapOptions, useUpdateMapOptions } from "../../../../hooks";
import { GeocodeProvider } from "../../../../context";

import { FormHelperText, GoogleMaps, Marker, useForm, useFormInputStyles, useFormRefs, useGoogleMaps } from "@hilma/netina2all";
import { provide } from "@hilma/tools";

import makeStyles from '@material-ui/core/styles/makeStyles';
import Skeleton from "@material-ui/lab/Skeleton";

import AutocompleteAddressInput from "./AutocompleteAddressInput";

import clsx from 'clsx';

export type AutocompletePrediction = google.maps.places.AutocompletePrediction;
export type AutocompleteService = google.maps.places.AutocompleteService;
export type PlacesServiceStatus = google.maps.places.PlacesServiceStatus;

export const autocompleteService: { current: AutocompleteService | null } = { current: null };

interface MapAddressSearchProps { }

const MapAddressSearch: React.FC<MapAddressSearchProps> = () => {
    const [selectedAddress, setSelectedAddress] = useState<AutocompletePrediction | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<AutocompletePrediction[]>([]);

    const isLoading = useGoogleMaps();

    // geocode context
    const [addressNotAcc, setAddressNotAcc] = useAddressNotAcc();
    const locationLoading = useLocationLoading();
    const addressLatLng = useAddressLatLng();
    const mapOptions = useMapOptions();
    const updateMapOptions = useUpdateMapOptions();
    const checkDistance = useCheckDistance();
    const geocode = useGeocode();

    const form = useForm();
    const innerRef = useFormRefs();

    const classes = useStyles()
    const formInputStyles = useFormInputStyles();

    const fromAutofill = useRef(true);

    const addressNotAccurate = useI18n(i18n => i18n.sm.address.addressNotAccurate);

    useEffect(() => {
        if (!fromAutofill.current) return;

        if (window.google && form.values.location.lat && form.values.location.lng) {
            const location = new google.maps.LatLng(form.values.location.lat, form.values.location.lng);
            updateMapOptions(location);
            geocodeCallback(form.values.address);

            if (fromAutofill.current) {
                fromAutofill.current = false;
            }
        }
    }, [form.values.location, window.google]);

    const error = useMemo(() => {
        return (form.touched.address && (!form.values.address || (!form.values.location.lat || !form.values.location.lng) || (form.touched.address && selectedAddress !== form.values['address']))) ? (form.errors.address as string | undefined) : null;
    }, [form, selectedAddress, addressLatLng]);

    const markers = useMemo<Marker[]>(() => {
        if (!addressLatLng) return [];
        const { lat, lng } = addressLatLng;
        return [{ lat: lat(), lng: lng(), color: '#AF1419' }];
    }, [addressLatLng]);

    const center = useMemo(() => {
        if (!selectedAddress && addressLatLng) return;
        return addressLatLng;
    }, [selectedAddress, addressLatLng]);

    const mapClick: (e: google.maps.MapMouseEvent) => void = ({ latLng, stop }) => {
        if (locationLoading) stop();
        setAddressNotAcc(false);
        executeGeocode(latLng);
    }

    const executeGeocode = (latLng: google.maps.LatLng | google.maps.LatLngLiteral) => {
        geocode({ location: latLng }, (results: google.maps.GeocoderResult) => {
            geocodeCallback(results?.formatted_address, latLng);
        })
    }

    const geocodeCallback = (input: string, latLng?: google.maps.LatLng | google.maps.LatLngLiteral) => {
        if (!autocompleteService.current && window.google) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return;
        }
        autocompleteService.current?.getPlacePredictions(
            { input }, (result: AutocompletePrediction[], status: PlacesServiceStatus) => {
                if (status === 'OK') {
                    setOptions(result);
                    setInputValue(result[0] ? result[0]?.description : "")
                    setSelectedAddress(result[0])

                    if (latLng) {
                        checkDistance(latLng, result[0]);
                        form.setFieldValue('address', result[0] ? result[0]?.description : "");
                        form.setFieldValue('inputOrMap', latLng ? 'map' : 'initial');
                    }
                }
            }
        );
    }

    return (
        <React.Fragment>
            {isLoading ?
                <Skeleton className={clsx(classes.addressMap)} variant='rect' width='100%' /> :
                <div className={classes.mapNotAccTextContainer}>
                    <GoogleMaps
                        className={clsx(classes.addressMap, (locationLoading && classes.loading), (addressNotAcc && classes.loadingEls), (error && formInputStyles.border))}
                        markers={markers}
                        mapOptions={mapOptions}
                        center={center}
                        onMapClick={mapClick}
                    >
                        <AutocompleteAddressInput
                            isLoading={isLoading}
                            selectedAddress={selectedAddress}
                            setSelectedAddress={setSelectedAddress}
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            options={options}
                            setOptions={setOptions}
                            innerRef={innerRef} />
                    </GoogleMaps>
                    {addressNotAcc &&
                        <div className={classes.notAccText}>
                            {addressNotAccurate}
                        </div>
                    }
                </div>
            }
            <FormHelperText text={error} error />
        </React.Fragment>
    );
}

export default provide(
    GeocodeProvider
)(MapAddressSearch);

const useStyles = makeStyles((theme) => ({
    addressMap: {
        width: '100%',
        height: 300,
        borderRadius: 4,
        marginTop: 5,
        overflow: 'hidden',
        position: 'relative',

        [theme.breakpoints.up(1600)]: {
            height: 400,
            borderRadius: 5,
        },

        [theme.breakpoints.down(670)]: {
            height: 250,
        },

        '&.loading': {
            opacity: 0.5,
        }
    },

    loading: {
        opacity: 0.5,
    },

    loadingEls: {
        '& div:nth-child(2)': {
            opacity: 0.5,
        },
    },

    mapNotAccTextContainer: {
        position: 'relative'
    },

    notAccText: {
        position: 'absolute',
        left: '0',
        top: '40%',
        width: '100%',
        margin: 'auto',
        fontFamily: 'Assistant-SemiBold',
        zIndex: 9,

        '& span': {
            width: '85%',
            margin: 'auto',
            whiteSpace: 'normal',
            fontFamily: 'Assistant-SemiBold',
            color: theme.palette.primary.main
        }
    }
}));